import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { Building2, Plus, Loader2, X, Users, CreditCard } from "lucide-react";
import { useToast } from "../../lib/toast";

export const TenantsList: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    rnc: "",
    business_type: "",
    industry: "",
    employee_count: 1,
    contact_email: "",
    contact_phone: "",
    address: "",
    city: "",
    subscription_tier: "OsirisBrain Business",
    monthly_rate: 49.99,
  });

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("tenants")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setTenants(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase.from("tenants").insert({
        ...form,
        is_active: true,
        billing_status: "active",
      });
      if (error) throw error;
      setShowModal(false);
      setForm({
        name: "",
        rnc: "",
        business_type: "",
        industry: "",
        employee_count: 1,
        contact_email: "",
        contact_phone: "",
        address: "",
        city: "",
        subscription_tier: "OsirisBrain Business",
        monthly_rate: 49.99,
      });
      fetchTenants();
    } catch (err) {
      console.error(err);
      toast.error("Error", "No se pudo crear el comercio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 p-8 overflow-y-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Gestión de Comercios
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Directorio completo de inquilinos con estado de cuenta y perfil de
            negocio
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-950 text-white font-bold rounded-xl text-sm transition-all shadow-md active:scale-95"
        >
          <Plus size={16} /> Nuevo Comercio
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-brand-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
            <Building2 size={20} className="text-brand-500" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase">
              Total Comercios
            </p>
            <p className="text-xl font-semibold text-gray-900">
              {tenants.length}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-brand-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
            <CreditCard size={20} className="text-brand-500" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase">Al Día</p>
            <p className="text-xl font-semibold text-gray-900">
              {tenants.filter((t) => t.billing_status === "active").length}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-brand-100 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <Users size={20} className="text-red-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase">
              Morosos / Inactivos
            </p>
            <p className="text-xl font-semibold text-gray-900">
              {
                tenants.filter(
                  (t) => t.billing_status !== "active" || !t.is_active,
                ).length
              }
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-brand-50 border-b border-brand-200 uppercase tracking-wider text-xs text-gray-500">
                <tr>
                  <th className="px-6 py-4 font-bold">Comercio</th>
                  <th className="px-6 py-4 font-bold">RNC</th>
                  <th className="px-6 py-4 font-bold">Tipo / Industria</th>
                  <th className="px-6 py-4 font-bold">Plan</th>
                  <th className="px-6 py-4 font-bold text-right">Tarifa</th>
                  <th className="px-6 py-4 font-bold text-right">Cuenta</th>
                  <th className="px-6 py-4 font-bold text-right">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tenants.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/admin/tenants/${t.id}`)}
                    className="hover:bg-brand-100 transition-colors cursor-pointer group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-brand-100 border border-brand-200 flex items-center justify-center group-hover:bg-brand-100 transition-colors">
                          <Building2 size={16} className="text-brand-500" />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">
                            {t.name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {t.city || "Sin ciudad"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-gray-500">
                      {t.rnc || "—"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-700">
                        {t.business_type || "—"}
                      </div>
                      <div className="text-xs text-gray-400">
                        {t.industry || ""}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-brand-100 text-gray-700 px-2.5 py-1 rounded-md text-xs font-bold uppercase">
                        {t.subscription_tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-normal text-gray-900">
                      ${t.monthly_rate || 0}/mo
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`px-2.5 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider ${
                          t.billing_status === "active"
                            ? "bg-brand-50 text-gray-700 border border-brand-100"
                            : t.billing_status === "overdue"
                              ? "bg-brand-50 text-gray-700 border border-brand-100"
                              : "bg-red-50 text-red-600 border border-red-100"
                        }`}
                      >
                        {t.billing_status === "active"
                          ? "Al Día"
                          : t.billing_status === "overdue"
                            ? "Moroso"
                            : "Suspendido"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`px-2.5 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider ${
                          t.is_active
                            ? "bg-gray-900 text-white"
                            : "bg-brand-100 text-gray-500"
                        }`}
                      >
                        {t.is_active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Creation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-brand-100 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-brand-100 bg-brand-50/50 sticky top-0 z-10">
              <h2 className="text-xl font-bold text-gray-900">
                Registrar Nuevo Comercio
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-brand-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Nombre del Comercio *
                  </label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                    placeholder="Ej: Distribuidora XYZ"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    RNC *
                  </label>
                  <input
                    required
                    value={form.rnc}
                    onChange={(e) => setForm({ ...form, rnc: e.target.value })}
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                    placeholder="Ej: 101000021"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Tipo de Negocio
                  </label>
                  <select
                    value={form.business_type}
                    onChange={(e) =>
                      setForm({ ...form, business_type: e.target.value })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  >
                    <option value="">Seleccionar...</option>
                    <option>Retail</option>
                    <option>Restaurante</option>
                    <option>Distribuidora</option>
                    <option>Farmacia</option>
                    <option>Ferretería</option>
                    <option>Clínica</option>
                    <option>Consultora</option>
                    <option>Manufactura</option>
                    <option>Servicios Profesionales</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Industria
                  </label>
                  <select
                    value={form.industry}
                    onChange={(e) =>
                      setForm({ ...form, industry: e.target.value })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  >
                    <option value="">Seleccionar...</option>
                    <option>Alimentos & Bebidas</option>
                    <option>Tecnología</option>
                    <option>Salud & Farmacéutica</option>
                    <option>Construcción</option>
                    <option>Comercio General</option>
                    <option>Educación</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Empleados
                  </label>
                  <input
                    type="number"
                    value={form.employee_count}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        employee_count: Number(e.target.value),
                      })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Email de Contacto
                  </label>
                  <input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) =>
                      setForm({ ...form, contact_email: e.target.value })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    value={form.contact_phone}
                    onChange={(e) =>
                      setForm({ ...form, contact_phone: e.target.value })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Ciudad
                  </label>
                  <input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Dirección
                  </label>
                  <input
                    value={form.address}
                    onChange={(e) =>
                      setForm({ ...form, address: e.target.value })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Plan
                  </label>
                  <select
                    value={form.subscription_tier}
                    onChange={(e) =>
                      setForm({ ...form, subscription_tier: e.target.value })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  >
                    <option>OsirisBrain Business</option>
                    <option>OsirisBrain Pro</option>
                    <option>OsirisBrain Enterprise</option>
                    <option>OsirisBrain Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Tarifa Mensual (USD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.monthly_rate}
                    onChange={(e) =>
                      setForm({ ...form, monthly_rate: Number(e.target.value) })
                    }
                    className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
                  />
                </div>
              </div>
              <div className="pt-4 border-t border-brand-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 rounded-xl font-bold text-brand-500 hover:bg-brand-100 transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl font-bold bg-gray-900 text-white hover:bg-gray-950 transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Registrar Comercio"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsList;
